/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * This context manages user authentication, token storage, and auth state.
 * 
 * Features:
 * - Centralized auth state management
 * - JWT token management with localStorage
 * - Auto-refresh token logic
 * - Protected route integration
 * - Loading and error states
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as loginUtil, logout as logoutUtil, isAuthenticated as isAuthenticatedUtil, getAuthUser, getAuthToken, setAuthToken, setAuthUser, clearAuth } from '../utils/auth';
import { loginApi, registerApi, verifyEmailApi, resendVerificationEmailApi, refreshTokenApi } from '../api/authApi';

// Create the AuthContext
const AuthContext = createContext(null);

/**
 * AuthContext Provider Component
 * Wraps the application and provides auth state and methods
 */
export const AuthProvider = ({ children }) => {
  // Auth state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize auth state on mount
   * Check if user is already authenticated from localStorage
   */
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const isAuth = isAuthenticatedUtil();
        const authUser = getAuthUser();
        const authToken = getAuthToken();

        setIsAuthenticated(isAuth);
        setUser(authUser);
        setToken(authToken);
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError('Failed to initialize authentication');
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isAuthenticated]);

  /**
   * Login function
   * Authenticates user and stores token/user data
   * 
   * @param {string} usernameOrEmail - Username or email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login response with token and user data
   * @throws {Error} If login fails
   */
  const login = useCallback(async (usernameOrEmail, password) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use API service layer instead of direct fetch
      const data = await loginApi(usernameOrEmail, password);

      // Store authentication data
      const loginSuccess = loginUtil(data.token, {
        id: data.id,
        username: data.username,
        email: data.email,
        userType: data.userType,
        firstName: data.firstName,
        lastName: data.lastName,
        institution: data.institution,
        emailVerified: data.emailVerified,
        createdAt: data.createdAt,
        themeId: data.themeId,
        appearanceMode: data.appearanceMode
      });

      if (!loginSuccess) {
        throw new Error('Failed to store authentication data');
      }

      // Update state
      setUser({
        id: data.id,
        username: data.username,
        email: data.email,
        userType: data.userType,
        firstName: data.firstName,
        lastName: data.lastName,
        institution: data.institution,
        emailVerified: data.emailVerified,
        createdAt: data.createdAt,
        themeId: data.themeId,
        appearanceMode: data.appearanceMode
      });
      setToken(data.token);
      setIsAuthenticated(true);

      return data;
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get role-based dashboard route
   * Returns the appropriate dashboard route based on user type
   * 
   * @param {string} userType - User type (STUDENT, TEACHER, PARENT, ADMIN, OWNER)
   * @returns {string} Dashboard route path
   */
  const getDashboardRoute = useCallback((userType) => {
    const routeMap = {
      STUDENT: '/dashboard/student',
      TEACHER: '/dashboard/teacher',
      PARENT: '/dashboard/parent',
      ADMIN: '/dashboard/admin',
      OWNER: '/dashboard/owner'
    };
    return routeMap[userType] || '/dashboard';
  }, []);

  /**
   * Register function
   * Creates a new user account
   * 
   * @param {Object} userData - User registration data
   * @param {string} userData.username - Username
   * @param {string} userData.email - Email address
   * @param {string} userData.password - Password
   * @param {string} userData.firstName - First name
   * @param {string} userData.lastName - Last name
   * @param {string} userData.userType - User type (STUDENT, TEACHER, PARENT, ADMIN, OWNER)
   * @param {string} userData.institution - Institution name
   * @returns {Promise<Object>} Registration response
   * @throws {Error} If registration fails
   */
  const register = useCallback(async (userData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use API service layer instead of direct fetch
      const data = await registerApi(userData);

      // Store authentication data if token is returned
      if (data.token) {
        const loginSuccess = loginUtil(data.token, data);
        if (!loginSuccess) {
          throw new Error('Failed to store authentication data');
        }

        setUser(data);
        setToken(data.token);
        setIsAuthenticated(true);
      }

      return data;
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Verify email function
   * Verifies user's email address using token
   * 
   * @param {string} token - Verification token from email
   * @returns {Promise<Object>} Verification response
   * @throws {Error} If verification fails
   */
  const verifyEmail = useCallback(async (token) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use API service layer instead of direct fetch
      const data = await verifyEmailApi(token);

      // Update user's email verification status
      if (user) {
        const updatedUser = { ...user, emailVerified: true };
        setUser(updatedUser);
        setAuthUser(updatedUser);
      }

      return data;
    } catch (err) {
      console.error('Email verification error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Resend verification email function
   * Requests a new verification email to be sent
   * 
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Response from server
   * @throws {Error} If resend fails
   */
  const resendVerificationEmail = useCallback(async (email) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use API service layer instead of direct fetch
      const data = await resendVerificationEmailApi(email);

      return data;
    } catch (err) {
      console.error('Resend verification error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh token function
   * Refreshes the authentication token
   * Note: This is a placeholder for future implementation
   * 
   * @returns {Promise<Object>} New token data
   * @throws {Error} If refresh fails
   */
  const refreshToken = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const currentToken = getAuthToken();
      if (!currentToken) {
        throw new Error('No token to refresh');
      }

      // Use API service layer instead of direct fetch
      const data = await refreshTokenApi();

      // Update token in storage and state
      setAuthToken(data.token);
      setToken(data.token);

      return data;
    } catch (err) {
      console.error('Refresh token error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout function
   * Clears authentication data and state
   */
  const logout = useCallback(() => {
    try {
      logoutUtil();
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Even if there's an error, clear state
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
    }
  }, []);

  /**
   * Update user data in state and storage
   * 
   * @param {Object} userData - Updated user data
   */
  const updateUser = useCallback((userData) => {
    try {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      setAuthUser(updatedUser);
    } catch (err) {
      console.error('Update user error:', err);
    }
  }, [user]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Auto-refresh token logic
   * Refreshes the authentication token every 4 minutes to prevent expiration
   */
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const refreshInterval = setInterval(async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Token refresh failed:', error);
        logout();
      }
    }, 4 * 60 * 1000); // 4 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, token, refreshToken, logout]);

  // Context value
  const value = {
    // State
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    // Methods
    login,
    register,
    logout,
    verifyEmail,
    resendVerificationEmail,
    refreshToken,
    updateUser,
    clearError,
    getDashboardRoute
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use AuthContext
 * Provides access to auth state and methods
 * 
 * @returns {Object} Auth context value
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;
